import React, {
  memo, useCallback, useMemo,
} from 'react';

import {
  bool, arrayOf, shape, string, func,
} from 'prop-types';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { ShopifyColorful } from '@picsio/icons';

import localization from '../../../../shared/strings';
import Logger from '../../../../services/Logger';
import { navigate } from '../../../../helpers/history';
import { PRODUCT_VIEW_LAYER } from '../../../AssetTargetsDialog/helpers/assetProductLinks';
import { resolveChannelLabel } from '../../../../store/products/helpers/showPublishedToChannelToast';
import { SHOPIFY_CHANNEL_TYPE } from '../../../teammates/views/Channels/helpers/salesChannelState';
import MediaAltText from '../../../CatalogItem/MediaAltText';
import { UNKNOWN_CHANNEL_ID } from '../ProductLinksSections/helpers/groupProductLinks';
import {
  resolveProductNavigation,
  resolveVariantNavigation,
} from '../ProductLinksSections/helpers/resolveProductLinkNavigation';
import CollapsibleGroup from '../CollapsibleGroup';
import buildAltTextUsageRows from './helpers/buildAltTextUsageRows';
import AltTextUsageChips from './helpers/AltTextUsageChips';
import AltTextDefaultField from './AltTextDefaultField';
import s from './styles.module.scss';

const AltTextSection = ({
  asset,
  products,
  productVariants,
  disabled,
  defaultAltDisabled,
  changeTree,
  openedTree,
}) => {
  const masterProducts = useSelector((state) => state.products.items);
  const teamSalesChannels = useSelector((state) => state.user?.team?.salesChannels || []);
  const strings = localization.ALT_TEXT;
  const detailsStrings = localization.DETAILS;

  const { masterRows, channelSections, hasUsageRows } = useMemo(
    () => buildAltTextUsageRows({
      products,
      productVariants,
      masterProducts,
      salesChannels: teamSalesChannels,
    }),
    [products, productVariants, masterProducts, teamSalesChannels],
  );

  const getChannelIcon = useCallback((channelId) => {
    if (channelId === UNKNOWN_CHANNEL_ID) return null;

    const salesChannel = teamSalesChannels.find(
      (channel) => String(channel._id) === String(channelId)
        || channel.type === channelId
        || channel.name === channelId,
    );

    if (salesChannel?.type === SHOPIFY_CHANNEL_TYPE) {
      return <ShopifyColorful />;
    }

    return null;
  }, [teamSalesChannels]);

  const getChannelTitle = useCallback((channelId) => {
    if (channelId === UNKNOWN_CHANNEL_ID) {
      return detailsStrings.textProductLinksUnknownChannel;
    }

    const label = resolveChannelLabel(channelId, teamSalesChannels);
    if (label) return label;
    return channelId.charAt(0).toUpperCase() + channelId.slice(1);
  }, [detailsStrings.textProductLinksUnknownChannel, teamSalesChannels]);

  const getChannelTitleInfo = useCallback((section) => {
    const hasLiveRow = section.rows.some(
      (row) => row.viewContext?.layer === PRODUCT_VIEW_LAYER.LIVE,
    );

    if (hasLiveRow) {
      return strings.publishedAltReadOnlyTooltip;
    }

    return strings.detailsChannelTooltip;
  }, [strings.detailsChannelTooltip, strings.publishedAltReadOnlyTooltip]);

  const handleNavigateProduct = useCallback((productLink) => {
    const navigation = resolveProductNavigation(productLink);
    if (!navigation?.masterProductId) return;

    Logger.log('User', 'InfoPanelProductClick', {
      masterProductId: navigation.masterProductId,
      productId: productLink._id,
    });

    navigate(`/product/${navigation.masterProductId}`);

    if (changeTree && openedTree !== 'products') {
      changeTree('products', true);
    }
  }, [changeTree, openedTree]);

  const handleNavigateVariant = useCallback((productVariant) => {
    const navigation = resolveVariantNavigation(productVariant);
    if (!navigation) return;

    Logger.log('User', 'InfoPanelProductVariantClick', {
      masterProductId: navigation.masterProductId,
      masterVariantId: navigation.masterVariantId,
      variantType: productVariant.type,
    });

    navigate(`/product/${navigation.masterProductId}/${navigation.masterVariantId}`);

    if (changeTree && openedTree !== 'products') {
      changeTree('products', true);
    }
  }, [changeTree, openedTree]);

  const renderUsageChips = useCallback((chips) => (
    <AltTextUsageChips
      chips={chips}
      onNavigateProduct={handleNavigateProduct}
      onNavigateVariant={handleNavigateVariant}
    />
  ), [handleNavigateProduct, handleNavigateVariant]);

  return (
    <div className={s.root} data-qa="alt-text-section">
      <AltTextDefaultField
        assetId={asset._id}
        value={asset.altText}
        disabled={defaultAltDisabled}
      />
      <If condition={hasUsageRows}>
        <div className={s.usageGroups}>
          <If condition={masterRows.length > 0}>
            <CollapsibleGroup title={detailsStrings.textProductLinksMaster}>
              {masterRows.map((row) => (
                <div
                  key={row.key}
                  className={cn(s.usageRow, { [s.usageRowNested]: row.isNested })}
                >
                  <MediaAltText
                    asset={asset}
                    productId={row.productId}
                    linkVariantId={row.linkVariantId}
                    isVariant={row.isVariant}
                    viewContext={row.viewContext}
                    productTitle={row.productTitle}
                    disabled={disabled}
                    layout="stack"
                    chips={renderUsageChips(row.chips)}
                  />
                </div>
              ))}
            </CollapsibleGroup>
          </If>
          {channelSections.map((section) => (
            <CollapsibleGroup
              key={section.channelId}
              title={getChannelTitle(section.channelId)}
              titleIcon={getChannelIcon(section.channelId)}
              titleInfo={getChannelTitleInfo(section)}
            >
              {section.rows.map((row) => (
                <div key={row.key} className={s.usageRow}>
                  <MediaAltText
                    asset={asset}
                    productId={row.productId}
                    linkVariantId={row.linkVariantId}
                    isVariant={row.isVariant}
                    viewContext={row.viewContext}
                    productTitle={row.productTitle}
                    disabled={disabled}
                    layout="stack"
                    chips={renderUsageChips(row.chips)}
                  />
                </div>
              ))}
            </CollapsibleGroup>
          ))}
        </div>
      </If>
    </div>
  );
};

AltTextSection.propTypes = {
  asset: shape({
    _id: string.isRequired,
    altText: string,
  }).isRequired,
  products: arrayOf(shape({
    _id: string,
    title: string,
    type: string,
    salesChannel: string,
    isDraft: bool,
  })),
  productVariants: arrayOf(shape({
    variantId: string,
    productId: string,
    title: string,
    type: string,
    isDraft: bool,
  })),
  disabled: bool,
  defaultAltDisabled: bool,
  changeTree: func,
  openedTree: string,
};

AltTextSection.defaultProps = {
  products: [],
  productVariants: [],
  disabled: false,
  defaultAltDisabled: false,
  changeTree: null,
  openedTree: '',
};

export default memo(AltTextSection);
